// Selbst-Reconciliation: Abgleich des BERECHNETEN Bestands gegen den vom Kunden
// gemeldeten echten Börsen-Saldo. Kernidee der Release-Reife-Doktrin: Die Software
// wird ihr eigener Auditor, statt sich auf das Soll-Wissen des Users zu verlassen.
//
// Rein & deterministisch — keine UI, kein State. Wird vom Review-Screen genutzt.

import { calcPortfolio } from './portfolioCalc';
import { normalizeToCanonical } from './canonicalNormalizer';
import { buildFifoPool } from './fifo';

// Toleranz: 0,5 % relativ deckt Rundung/Staub ab, ohne echte Abweichungen zu
// verschlucken (heutige Fehler lagen bei 30 %+ / ganzen Positionen). Absolute
// Untergrenze fängt Sub-Cent-Mengen ab.
const DEFAULT_REL_TOL = 0.005;
const DEFAULT_ABS_TOL = 1e-8;

/** Symbole, die in einer TX-Liste vorkommen (coin + swap-Seiten), UPPERCASE. */
export function coinsInTxs(txs) {
  const set = new Set();
  for (const tx of txs || []) {
    const add = (s) => { if (s) set.add(String(s).trim().toUpperCase()); };
    if (tx.typ === 'swap') { add(tx.coinIn || tx.symIn); add(tx.coinOut || tx.symOut); }
    else add(tx.coin || tx.sym || tx.asset);
  }
  return set;
}

/**
 * Berechnete Bestände NACH Anwendung des Imports (bestehende + neue TX).
 * @returns {{ [sym]: number }} Menge je Coin (nur > 0).
 */
export function holdingsAfterImport(existingTxs = [], newTxs = []) {
  const combined = [...(existingTxs || []), ...(newTxs || [])].map(normalizeToCanonical);
  const pf = calcPortfolio(combined);
  const out = {};
  for (const sym of Object.keys(pf)) out[sym] = pf[sym].menge;
  return out;
}

/**
 * Vergleicht berechneten Bestand mit dem echten (vom User gemeldeten) Saldo.
 * @returns {{status:'match'|'mismatch'|'unknown', delta:number, deltaPct:number}}
 */
export function compareHolding(computed, real, opts = {}) {
  const relTol = opts.relTol ?? DEFAULT_REL_TOL;
  const absTol = opts.absTol ?? DEFAULT_ABS_TOL;
  const c = Number(computed) || 0;
  // null/undefined/'' = kein Ist-Wert eingegeben (Number(null)===0 würde sonst
  // fälschlich als 0-Saldo verglichen → Fehlalarm).
  if (real === null || real === undefined || real === '') {
    return { status: 'unknown', delta: 0, deltaPct: 0 };
  }
  const r = Number(real);
  if (!Number.isFinite(r)) return { status: 'unknown', delta: 0, deltaPct: 0 };
  const delta = c - r;
  const ad = Math.abs(delta);
  if (ad <= absTol) return { status: 'match', delta: 0, deltaPct: 0 };
  const rel = ad / Math.max(Math.abs(c), Math.abs(r), 1e-12);
  return {
    status: rel <= relTol ? 'match' : 'mismatch',
    delta,
    deltaPct: rel * 100,
  };
}

/**
 * Baut den Abgleichs-Report für die gerade importierten Coins.
 * @param {Array} existingTxs  Bestand in Firestore
 * @param {Array} newTxs       zu importierende (bereinigte) TX
 * @param {{[sym]:number|string}} realBalances  vom User eingegebene Ist-Salden (optional)
 * @returns {Array<{sym, computed, real, status, delta, deltaPct}>} sortiert: Abweichungen zuerst
 */
export function buildReconciliation(existingTxs, newTxs, realBalances = {}) {
  const holdings = holdingsAfterImport(existingTxs, newTxs);
  const relevant = coinsInTxs(newTxs);
  const rows = [];
  for (const sym of relevant) {
    // Fiat o.ä. fällt in calcPortfolio raus → computed evtl. undefined → 0.
    const computed = holdings[sym] ?? 0;
    const realRaw = realBalances[sym];
    const real = (realRaw === '' || realRaw == null) ? null
      : Number(String(realRaw).replace(',', '.'));
    const cmp = compareHolding(computed, real);
    rows.push({ sym, computed, real, ...cmp });
  }
  // Abweichungen nach oben, dann unbestätigte, dann Treffer; je Gruppe alphabetisch.
  const rank = { mismatch: 0, unknown: 1, match: 2 };
  rows.sort((a, b) => (rank[a.status] - rank[b.status]) || (a.sym < b.sym ? -1 : 1));
  return rows;
}

// ── Automatischer Hintergrund-Audit (KEIN Kunden-Input nötig) ───────────────
// Prüft den Import auf interne Inkonsistenzen, die KEIN Soll-Wissen erfordern.
// Idee: die gefährlichsten Fehler (Reihenfolge-Abhängigkeit, Negativbestand,
// fehlende Preise) erkennt die Software an sich selbst. Der Kunde tut nichts —
// es wird nur gewarnt, wenn wirklich etwas auffällig ist.

function poolHoldings(pool) {
  const out = {};
  for (const coin of Object.keys(pool || {})) {
    let sum = 0;
    for (const lot of pool[coin]) sum += Number(lot.rest) || 0; // inkl. Debt-Lots (negativ)
    out[coin] = sum;
  }
  return out;
}

/**
 * Automatischer Import-Audit. Liefert eine Liste von Findings (leer = alles gut).
 * @returns {Array<{severity:'critical'|'warning'|'info', code, title, detail, coins?}>}
 */
export function auditImport(existingTxs = [], newTxs = []) {
  const findings = [];
  const combined = [...(existingTxs || []), ...(newTxs || [])].map(normalizeToCanonical);
  if (combined.length === 0) return findings;

  // 1. DETERMINISMUS (Invariant 7) — der wichtigste Auto-Check. buildFifoPool
  //    normalisiert die Reihenfolge intern; ein stabiles Ergebnis muss daher
  //    unabhängig von der Eingabe-Reihenfolge sein. Wir bauen den Pool in drei
  //    Reihenfolgen (original, umgekehrt, datum-desc = Firestore-Order) und
  //    vergleichen die Bestände. Weicht etwas ab → Berechnungsfehler (genau die
  //    XRP-8001→16363-Klasse), vollautomatisch erkannt, ohne Kunden-Wissen.
  let poolOrig;
  try {
    poolOrig = buildFifoPool(combined).pool;
    const hOrig = poolHoldings(poolOrig);
    const orders = [
      [...combined].reverse(),
      [...combined].sort((a, b) => {
        const da = String(a.datum || ''), db = String(b.datum || '');
        return da < db ? 1 : da > db ? -1 : 0; // desc
      }),
    ];
    const drift = new Set();
    for (const ord of orders) {
      const h = poolHoldings(buildFifoPool(ord).pool);
      const coins = new Set([...Object.keys(hOrig), ...Object.keys(h)]);
      for (const c of coins) {
        const a = hOrig[c] || 0, b = h[c] || 0;
        const base = Math.max(Math.abs(a), Math.abs(b), 1e-9);
        if (Math.abs(a - b) / base > 1e-6) drift.add(c);
      }
    }
    if (drift.size > 0) {
      findings.push({
        severity: 'critical', code: 'nondeterministic', coins: [...drift].sort(),
        title: 'Berechnung nicht stabil',
        detail: `Der Bestand von ${[...drift].sort().join(', ')} hängt von der Reihenfolge der Transaktionen ab — das ist ein Berechnungsfehler, nicht deine Schuld. Bitte noch nicht speichern und uns diesen Import melden.`,
      });
    }
  } catch { /* Build-Fehler unten separat nicht nötig — Audit ist best-effort */ }

  // 2. NEGATIVBESTAND (Invariant 2) — mehr verkauft/gesendet als vorhanden →
  //    fehlt eine Quelle (Wallet/CSV) oder Doppelbuchung.
  if (poolOrig) {
    const h = poolHoldings(poolOrig);
    const neg = Object.keys(h).filter(c => h[c] < -1e-6).sort();
    if (neg.length > 0) {
      findings.push({
        severity: 'warning', code: 'negative_balance', coins: neg,
        title: 'Negativer Bestand',
        detail: `Bei ${neg.join(', ')} wurde mehr ausgegeben als vorhanden. Meist fehlt noch eine Quelle (eine Börse/Wallet, von der die Coins kamen).`,
      });
    }

    // 3. FEHLENDE ANSCHAFFUNGSWERTE — hoher Anteil costBasisUnknown deutet auf
    //    eine fehlende Quelle oder einen nicht erkannten Preis hin.
    let totalPos = 0, unknownPos = 0;
    for (const coin of Object.keys(poolOrig)) {
      for (const lot of poolOrig[coin]) {
        const r = Number(lot.rest) || 0;
        if (r <= 1e-9) continue;
        totalPos += r > 0 ? 1 : 0; // pro Lot zählen
        if (lot.costBasisUnknown) unknownPos += 1;
      }
    }
    if (totalPos > 0 && unknownPos / totalPos > 0.3) {
      findings.push({
        severity: 'info', code: 'cost_basis_unknown',
        unknownPos, totalPos,
        title: 'Viele Positionen ohne Anschaffungswert',
        detail: `${unknownPos} von ${totalPos} offenen Positionen haben keinen Kaufpreis. Das senkt die Genauigkeit der Gewinn-/Verlust-Anzeige — evtl. fehlt eine Quelle oder ein historischer Preis.`,
      });
    }
  }

  // 4. PREISLOSE TRADES IM IMPORT — kauf/verkauf mit preis=0 deutet auf ein nicht
  //    korrekt erkanntes Format hin (genau das Bitvavo-/Coinbase-0€-Symptom).
  const fiat = new Set(['EUR', 'USD', 'GBP', 'CHF']);
  const zeroPriceTrades = (newTxs || []).filter(t =>
    (t.typ === 'kauf' || t.typ === 'verkauf') &&
    !(Number(t.preis) > 0) &&
    !fiat.has(String(t.coin || t.sym || '').toUpperCase()),
  ).length;
  if (zeroPriceTrades > 0) {
    findings.push({
      severity: 'warning', code: 'zero_price_trades',
      count: zeroPriceTrades,
      title: 'Käufe/Verkäufe ohne Preis',
      detail: `${zeroPriceTrades} Trade(s) in diesem Import haben keinen EUR-Preis. Möglicherweise wurde das CSV-Format nicht vollständig erkannt.`,
    });
  }

  // Schwere zuerst.
  const sev = { critical: 0, warning: 1, info: 2 };
  findings.sort((a, b) => sev[a.severity] - sev[b.severity]);
  return findings;
}
